import { firestoreService } from './firestoreService';
import { auth } from './firebaseConfig';

export interface WallpaperHistoryItem {
  id: string;
  title: string;
  style: string;
  createdAt: string;
  thumbnail: string;
  fullImageUrl: string;
  isWatermarked: boolean;
  downloads: number;
  prompt?: string;
  dimensions: {
    width: number;
    height: number;
  };
}

export const historyService = {
  // 获取历史记录（从 Firestore，分别获取最近50次生成和50次在线）
  getHistory: async (): Promise<WallpaperHistoryItem[]> => {
    try {
      console.log('🔥 开始从 Firestore 获取历史记录...');
      const currentUser = auth.currentUser;
      console.log('🔥 当前用户:', currentUser);
      
      if (!currentUser) {
        console.warn('❌ 用户未登录，无法获取历史记录');
        return [];
      }

      console.log('🔥 分别获取生成和在线壁纸，用户ID:', currentUser.uid);
      
      // 并行获取两种类型的壁纸
      const [generatedWallpapers, onlineWallpapers] = await Promise.all([
        firestoreService.getUserWallpapers(currentUser.uid, 'generated', 50),
        firestoreService.getUserWallpapers(currentUser.uid, 'online', 50)
      ]);
      
      console.log('🔥 生成壁纸数量:', generatedWallpapers.length);
      console.log('🔥 在线壁纸数量:', onlineWallpapers.length);
      
      // 合并两种类型的壁纸
      const allWallpapers = [...generatedWallpapers, ...onlineWallpapers];
      
      // 按创建时间排序（最新的在前）
      allWallpapers.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      
      // 转换为前端需要的格式
      const formattedWallpapers = allWallpapers.map((wp) => ({
        id: wp.id,
        title: wp.title,
        style: wp.style,
        createdAt: wp.createdAt,
        thumbnail: wp.thumbnail,
        fullImageUrl: wp.fullImageUrl,
        isWatermarked: wp.isWatermarked,
        downloads: wp.downloads,
        prompt: wp.prompt || '',
        dimensions: wp.dimensions
      }));
      
      console.log('🔥 格式化后的壁纸数据总数:', formattedWallpapers.length);
      return formattedWallpapers;
    } catch (error) {
      console.error('❌ 从 Firestore 获取历史记录失败:', error);
      if (error instanceof Error) {
        console.error('❌ 错误详情:', error.message);
      }
      return [];
    }
  },

  // 删除历史记录项（从 Firestore）
  deleteWallpaper: async (id: string): Promise<void> => {
    try {
      await firestoreService.deleteWallpaper(id);
      console.log(`Wallpaper ${id} deleted from Firestore`);
    } catch (error) {
      console.error('Error deleting wallpaper from Firestore:', error);
      throw error;
    }
  },

  // 清空历史记录（从 Firestore）
  clearHistory: async (): Promise<void> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      await firestoreService.clearUserWallpapers(currentUser.uid);
      console.log('All wallpaper history cleared from Firestore');
    } catch (error) {
      console.error('Error clearing wallpaper history from Firestore:', error);
      throw error;
    }
  },

  // 增加下载次数（保存到 Firestore）
  incrementDownloads: async (id: string): Promise<void> => {
    try {
      await firestoreService.incrementWallpaperDownloads(id);
      console.log(`Download count incremented for wallpaper: ${id}`);
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  },

  // 保存在线搜索的壁纸
  saveOnlineWallpaper: async (wallpaper: {
    title: string;
    imageUrl: string;
    thumbnailUrl: string;
    author?: string;
    sourceId?: string;
  }): Promise<{ success: boolean; message?: string; remainingSlots?: number }> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { success: false, message: '用户未登录' };
      }

      // 检查壁纸是否已存在
      const exists = await firestoreService.checkWallpaperExists(currentUser.uid, wallpaper.imageUrl);
      if (exists) {
        return {
          success: false,
          message: 'This wallpaper has already been saved.'
        };
      }

      // 不再需要检查数量限制，因为保存后会自动清理超出限制的壁纸

      // 保存壁纸到 Firestore
      await firestoreService.saveWallpaper({
        userId: currentUser.uid,
        title: wallpaper.title,
        style: 'online',
        thumbnail: wallpaper.thumbnailUrl,
        fullImageUrl: wallpaper.imageUrl,
        isWatermarked: false,
        downloads: 0,
        dimensions: {
          width: 1170,
          height: 2532
        }
      });

      console.log('Online wallpaper saved successfully to Firestore');
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Error saving online wallpaper:', error);
      return {
        success: false,
        message: 'Failed to save wallpaper. Please try again.'
      };
    }
  },

  // 清理localStorage（保留用于清理旧数据）
  cleanupStorage: (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('wallpaper') || key.includes('history')) {
          const value = localStorage.getItem(key);
          if (value && value.length > 100000) {
            console.log(`Removing large data from localStorage: ${key}`);
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Error cleaning up localStorage:', error);
    }
  }
};
